import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FileText, ChevronDown, ChevronRight, Plus, Download, AlertCircle, Loader2, Building2 } from 'lucide-react';
import { supabase } from '../supabaseClient';
import EntityLink from './shared/EntityLink';
import { formatDate } from '../utils/dateHelpers';

/**
 * Intake-1 (ADR-013) — Zona 3 (Transação) do OfertaDetailModal.
 *
 * 1 oferta : N propostas. Cada proposta carrega:
 *   - proponente (empresa)
 *   - imóveis cobertos (proposta_imoveis → imoveis)
 *   - documentos inline (v_entity_documents WHERE entity_type='proposta')
 *
 * Não duplica os document_links que ainda existem em entity_type='oferta'
 * (backward compatible) — quem renderiza a "Outros documentos" filtra
 * pelos que NÃO estão atrelados a nenhuma proposta.
 */

const STATUS_COLORS = {
  draft:          'bg-slate-100 text-slate-700',
  under_review:   'bg-amber-100 text-amber-800',
  presented:      'bg-blue-100 text-blue-800',
  in_negotiation: 'bg-purple-100 text-purple-800',
  accepted:       'bg-emerald-100 text-emerald-800',
  rejected:       'bg-red-100 text-red-800',
  cancelled:      'bg-slate-200 text-slate-600',
  expired:        'bg-orange-100 text-orange-800',
};
const STATUS_LABELS = {
  draft: 'Rascunho', under_review: 'Em análise', presented: 'Apresentada',
  in_negotiation: 'Em negociação', accepted: 'Aceita', rejected: 'Recusada',
  cancelled: 'Cancelada', expired: 'Expirada',
};
const TIPO_COLORS = {
  purchase:        'bg-indigo-100 text-indigo-800',
  physical_swap:   'bg-teal-100 text-teal-800',
  financial_swap:  'bg-cyan-100 text-cyan-800',
  mixed:           'bg-violet-100 text-violet-800',
  partnership:     'bg-pink-100 text-pink-800',
};
const TIPO_LABELS = {
  purchase: 'Compra', physical_swap: 'Permuta física',
  financial_swap: 'Permuta financeira', mixed: 'Mista', partnership: 'Parceria',
};

const fmtMoney = (v) => {
  if (v == null) return null;
  const n = Number(v);
  if (!isFinite(n) || n === 0) return null;
  if (n >= 1e6) return `R$ ${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `R$ ${(n / 1e3).toFixed(0)}k`;
  return `R$ ${n.toLocaleString('pt-BR')}`;
};

const ProposalCard = ({ proposta, docs, downloadAttachment, tiposDocumentoMap }) => {
  const proponenteNome = proposta.proponente?.empresa
    || proposta.proponente?.company_name
    || null;
  const imoveis = (proposta.proposta_imoveis || [])
    .map(pi => pi.imovel)
    .filter(Boolean);
  const valor = fmtMoney(proposta.valor_proposto);
  const valorM2 = fmtMoney(proposta.valor_m2);
  const statusClass = STATUS_COLORS[proposta.status] || 'bg-slate-100 text-slate-700';
  const tipoClass = TIPO_COLORS[proposta.tipo] || 'bg-slate-100 text-slate-700';
  const dataLabel = proposta.data_proposta ? formatDate(proposta.data_proposta) : null;

  return (
    <div className="px-4 py-3 border-b border-slate-100 last:border-b-0">
      {/* Linha 1: nome + data + tipo + status */}
      <div className="flex items-center gap-2 flex-wrap mb-1.5">
        <FileText size={13} className="text-indigo-600 flex-shrink-0" />
        <span className="text-sm font-semibold text-slate-800 truncate">{proposta.nome || '(sem nome)'}</span>
        {dataLabel && <span className="text-[11px] text-slate-500">{dataLabel}</span>}
        <span className="flex-1" />
        {proposta.tipo && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${tipoClass}`}>
            {TIPO_LABELS[proposta.tipo] || proposta.tipo}
          </span>
        )}
        {proposta.status && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusClass}`}>
            {STATUS_LABELS[proposta.status] || proposta.status}
          </span>
        )}
      </div>

      {/* Linha 2: proponente + valores */}
      <div className="flex items-center gap-3 flex-wrap mb-1 text-xs">
        {proponenteNome && (
          <span className="flex items-center gap-1 text-slate-700">
            <Building2 size={11} className="text-slate-400" />
            {proposta.proponente_empresa_id ? (
              <EntityLink
                entityType="empresa"
                entityId={proposta.proponente_empresa_id}
                label={proponenteNome}
                showIcon={false}
              />
            ) : (
              <span>{proponenteNome}</span>
            )}
          </span>
        )}
        {valor && <span className="text-emerald-700 font-medium">💰 {valor}</span>}
        {valorM2 && <span className="text-slate-500">({valorM2}/m²)</span>}
        {proposta.percentual_permuta != null && (
          <span className="text-slate-500">Permuta: {Number(proposta.percentual_permuta)}%</span>
        )}
      </div>

      {/* Linha 3: condicoes (truncado) */}
      {proposta.condicoes_pagamento && (
        <div className="text-xs text-slate-600 mb-1 line-clamp-2" title={proposta.condicoes_pagamento}>
          <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">Condições:</span>
          {proposta.condicoes_pagamento}
        </div>
      )}

      {/* Linha 4: imoveis cobertos (tags) */}
      {imoveis.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          <span className="text-[10px] text-slate-400 uppercase tracking-wider mr-0.5 flex items-center">Imóveis:</span>
          {imoveis.map(im => (
            <EntityLink
              key={im.id}
              entityType="imovel"
              entityId={im.id}
              label={im.nome || '(sem nome)'}
              className="text-[11px] px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded inline-flex items-center gap-1"
              showIcon={false}
            />
          ))}
        </div>
      )}

      {/* Linha 5: documentos inline */}
      {docs.length > 0 && (
        <div className="mt-2 space-y-0.5">
          {docs.map(d => (
            <div
              key={d.attachment_id}
              className="flex items-center gap-2 px-2 py-1 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded text-xs"
            >
              <FileText size={11} className="text-slate-400 flex-shrink-0" />
              <span className="truncate flex-grow text-slate-700" title={d.filename || ''}>
                {d.filename || '(sem nome)'}
              </span>
              {d.document_type && (
                <span className="text-[10px] px-1 bg-white rounded whitespace-nowrap border border-slate-200">
                  {tiposDocumentoMap?.get(d.document_type) || d.document_type}
                </span>
              )}
              <button
                type="button"
                onClick={() => downloadAttachment({ storage_path: d.storage_path, filename: d.filename })}
                disabled={!d.storage_path}
                title="Baixar"
                className="p-0.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded disabled:opacity-30"
              >
                <Download size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProposalSection = ({ ofertaId, downloadAttachment, tiposDocumentoMap, refreshKey }) => {
  const [propostas, setPropostas] = useState([]);
  const [docsByProposta, setDocsByProposta] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!ofertaId) {
      setPropostas([]);
      setDocsByProposta(new Map());
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: propData, error: propErr } = await supabase
        .from('propostas')
        .select(`
          id, nome, tipo, status, valor_proposto, valor_m2,
          condicoes_pagamento, percentual_permuta, data_proposta,
          proponente_empresa_id, notas, created_at,
          proponente:empresas!propostas_proponente_empresa_id_fkey(id, empresa, company_name),
          proposta_imoveis(
            id,
            imovel:imoveis(id, nome)
          )
        `)
        .eq('oferta_id', ofertaId)
        .order('data_proposta', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });
      if (propErr) throw propErr;

      const ps = propData || [];
      setPropostas(ps);

      // Carrega docs para todas as propostas em uma query só
      const ids = ps.map(p => p.id);
      if (ids.length === 0) {
        setDocsByProposta(new Map());
      } else {
        const { data: docsData, error: docsErr } = await supabase
          .from('v_entity_documents')
          .select('*')
          .eq('entity_type', 'proposta')
          .in('entity_id', ids)
          .order('created_at', { ascending: false });
        if (docsErr) throw docsErr;
        const map = new Map();
        for (const d of (docsData || [])) {
          if (!map.has(d.entity_id)) map.set(d.entity_id, []);
          map.get(d.entity_id).push(d);
        }
        setDocsByProposta(map);
      }
    } catch (err) {
      console.error('[ProposalSection] fetch falhou:', err);
      setError(err.message || String(err));
      setPropostas([]);
      setDocsByProposta(new Map());
    } finally {
      setLoading(false);
    }
  }, [ofertaId]);

  useEffect(() => { fetchAll(); }, [fetchAll, refreshKey]);

  const total = propostas.length;

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900"
        >
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <FileText size={12} className="text-indigo-600" />
          Propostas
          <span className="text-[10px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
            {loading ? '…' : total}
          </span>
        </button>
        <button
          type="button"
          disabled
          title="Em breve"
          className="px-2 py-1 text-[11px] bg-slate-100 text-slate-400 rounded border border-slate-200 flex items-center gap-1 cursor-not-allowed"
        >
          <Plus size={11} /> Nova proposta
        </button>
      </div>

      {error && (
        <div className="mb-2 flex items-start gap-1.5 p-2 bg-red-50 border border-red-200 rounded text-[11px] text-red-800">
          <AlertCircle size={11} className="flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {expanded && (
        loading ? (
          <div className="bg-white border border-slate-200 rounded p-3 flex items-center gap-2 text-xs text-slate-500">
            <Loader2 size={12} className="animate-spin" /> Carregando propostas...
          </div>
        ) : total === 0 ? (
          <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-500 italic">
            Nenhuma proposta registrada para esta oferta.
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded">
            {propostas.map(p => (
              <ProposalCard
                key={p.id}
                proposta={p}
                docs={docsByProposta.get(p.id) || []}
                downloadAttachment={downloadAttachment}
                tiposDocumentoMap={tiposDocumentoMap}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
};

/** IDs de attachments que JÁ estão atrelados a alguma proposta — útil para
 *  o "Outros documentos" da Zona 4/5 filtrar e não duplicar. */
export const useAttachmentIdsLinkedToPropostas = (ofertaId) => {
  const [ids, setIds] = useState(() => new Set());
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!ofertaId) { setIds(new Set()); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data: ps } = await supabase
          .from('propostas').select('id').eq('oferta_id', ofertaId);
        const propIds = (ps || []).map(p => p.id);
        if (propIds.length === 0) {
          if (!cancelled) setIds(new Set());
          return;
        }
        const { data: dl } = await supabase
          .from('document_links')
          .select('attachment_id')
          .eq('entity_type', 'proposta')
          .in('entity_id', propIds);
        if (cancelled) return;
        setIds(new Set((dl || []).map(r => r.attachment_id)));
      } catch (err) {
        console.error('[useAttachmentIdsLinkedToPropostas]', err);
        if (!cancelled) setIds(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [ofertaId, version]);

  const refetch = useMemo(() => () => setVersion(v => v + 1), []);
  return { proposalAttachmentIds: ids, refetch };
};

export default ProposalSection;
